import type {
  DeactivateProfilesResponse,
  DeduplicateListResponse,
  ImportListEmailsResponse,
  ListDuplicatesResponse,
  ListResponse,
  ProfileResponse,
  RestoreProfilesResponse,
  SetExpertValidationResponse,
} from '@shared/schemas';

import api from '@/shared/api/api-client';

import { httpListDetailQueryGateway } from '../../_shared/infrastructure/list-detail-query.http';
import type { ListDetailGateway } from './list-detail.gateway';

/**
 * Real implementation over the shared api client: the single place that knows
 * the admin-only List Detail endpoints and request shapes (kept 1:1 with the
 * original component). The four common reads come from the shared query http
 * gateway (also bound by the client Discovered LP Detail slice).
 * `api.get/post/delete` unwrap the `{ success, data }` envelope.
 */
export const httpListDetailGateway: ListDetailGateway = {
  ...httpListDetailQueryGateway,

  loadRemovedProfiles: (listId) =>
    api.get<ProfileResponse[]>(`/lists/${listId}/removed-profiles?limit=5000`),

  createEnrichmentSession: async ({ listId, licenceId, profileIds, force }) => {
    await api.post('/enrichment-sessions', {
      type: 'linkedin_standard',
      licence_id: licenceId,
      list_id: listId,
      profile_ids: profileIds,
      ...(force ? { force: true } : {}),
    });
  },

  deactivateProfiles: ({ profileIds, scope, listId }) =>
    api.post<DeactivateProfilesResponse>('/profiles/deactivate', {
      profile_ids: profileIds,
      scope,
      ...(scope === 'list' && listId ? { list_id: listId } : {}),
    }),

  restoreProfiles: (listId, profileIds) =>
    api.post<RestoreProfilesResponse>(`/lists/${listId}/restore-profiles`, {
      profile_ids: profileIds,
    }),

  setExpertValidation: (profileIds, validated) =>
    api.post<SetExpertValidationResponse>('/profiles/expert-validation', {
      profile_ids: profileIds,
      validated,
    }),

  deleteList: async (listId) => {
    await api.delete(`/admin/lists/${listId}`);
  },

  importEmails: (listId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.upload<ImportListEmailsResponse>(`/admin/lists/${listId}/import-emails`, formData);
  },
  findDuplicates: (listId, scope) =>
    api.get<ListDuplicatesResponse>(`/lists/${listId}/duplicates?scope=${scope}`),

  removeDuplicates: (listId, scope) =>
    api.post<DeduplicateListResponse>(`/lists/${listId}/deduplicate`, { scope }),

  // The same read as the licence's Lists tab: every origin, client imports included.
  loadLicenceListNames: async (licenceId) => {
    const lists = await api.get<ListResponse[]>(`/lists?licence_id=${licenceId}&origin=all`);
    return lists.map((l) => l.name);
  },

  duplicateList: (listId, name) =>
    api.post<ListResponse>(`/admin/lists/${listId}/duplicate`, { name }),
};
